// src/pages/ResourcesPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useData } from "../DataContext";
import { useTranslation } from "react-i18next";

import { Card } from "../components/ui/Card";
import { TableShell, CellClamp } from "../components/ui/TableShell";

import { apiRequest } from "../api/http";

const money0 = (n: number) => `$${(Number(n) || 0).toLocaleString()}`;

// ---- API types (Workers table in D1) ----
type ApiWorker = {
  id: string;
  tenant_id: string;
  name: string;
  role: string | null;
  phone: string | null;
  email?: string | null;
  company_name?: string | null;
  notes?: string | null;
  rate: number | null;
  is_subcontractor: number; // 0/1
  created_at: string;
  updated_at: string;
};

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

// lightweight inline icons (no deps)
function IconPencil({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconTrash({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6l1 16h8l1-16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

  export default function ResourcesPage() {
  const { tenantId, reloadResourcesFromApi } = useData() as any;
  const { t } = useTranslation();

  /* ======================
     Page controls (searches)
  ====================== */
  const [workerSearch, setWorkerSearch] = useState("");
  const [scSearch, setScSearch] = useState("");

  /* ======================
     WORKERS (API-backed)
  ====================== */
  const [workers, setWorkers] = useState<ApiWorker[]>([]);
  const [workersLoading, setWorkersLoading] = useState(false);
  const [workersError, setWorkersError] = useState<string | null>(null);

  const reloadWorkers = async () => {
    setWorkersLoading(true);
    setWorkersError(null);
    try {
      const res = await apiRequest<ApiOk<ApiWorker[]> | ApiErr>("GET", "/api/resources", {
        tenantId,
      });

      if ((res as any).ok) {
        const all = ((res as any).data || []) as ApiWorker[];
        setWorkers(all.filter((r) => Number(r.is_subcontractor) === 0));
      } else {
        setWorkersError((res as any).error || "Failed to load workers");
      }
    } catch (e: any) {
      setWorkersError(e?.message || "Failed to load workers");
    } finally {
      setWorkersLoading(false);
    }
  };

  useEffect(() => {
    reloadWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [dayRate, setDayRate] = useState("");

  const [editingWorkerId, setEditingWorkerId] = useState<string | null>(null);
  const [editWorkerName, setEditWorkerName] = useState("");
  const [editWorkerRole, setEditWorkerRole] = useState("");
  const [editWorkerDayRate, setEditWorkerDayRate] = useState("");

  const handleAddWorker = async () => {
    if (!name.trim() || !dayRate) {
      alert(t("resources.workers.nameRateRequired"));
      return;
    }
    const rate = Number(dayRate);
    if (Number.isNaN(rate) || rate < 0) {
      alert(t("resources.workers.dayRateInvalid"));
      return;
    }

    try {
      const body = {
        name: name.trim(),
        role: role.trim(),
        phone: "",
        email: "",
        company_name: "",
        notes: "",
        rate,
        is_subcontractor: 0,
      };

      const res = await apiRequest<ApiOk<ApiWorker> | ApiErr>("POST", "/api/resources", {
        tenantId,
        body,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to add worker");
        return;
      }

      // Insert at top (API returns row)
      const row = (res as any).data as ApiWorker;
      setWorkers((prev) => [row, ...prev]);
      await reloadResourcesFromApi?.();
      await Promise.allSettled([reloadWorkers(), reloadSubcontractors()]);
      setName("");
      setRole("");
      setDayRate("");
    } catch (e: any) {
      alert(e?.message || "Failed to add worker");
    }
  };

  const startEditWorker = (r: ApiWorker) => {
    setEditingWorkerId(r.id);
    setEditWorkerName(r.name || "");
    setEditWorkerRole(r.role || "");
    setEditWorkerDayRate(String(r.rate ?? ""));
  };

  const cancelEditWorker = () => {
    setEditingWorkerId(null);
    setEditWorkerName("");
    setEditWorkerRole("");
    setEditWorkerDayRate("");
  };

  const saveEditWorker = async (r: ApiWorker) => {
    if (!editWorkerName.trim() || !editWorkerDayRate) {
      alert(t("resources.workers.nameRateRequired"));
      return;
    }
    const rate = Number(editWorkerDayRate);
    if (Number.isNaN(rate) || rate < 0) {
      alert(t("resources.workers.dayRateInvalid"));
      return;
    }

    try {
      const patch = {
        name: editWorkerName.trim(),
        role: editWorkerRole.trim(),
        rate,
      };

      const res = await apiRequest<ApiOk<ApiWorker> | ApiErr>("PATCH", `/api/resources/${r.id}`, {
        tenantId,
        body: patch,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update worker");
        return;
      }

      const updated = (res as any).data as ApiWorker;
      setWorkers((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
      await reloadResourcesFromApi?.();
      await Promise.allSettled([reloadWorkers(), reloadSubcontractors()]);
      cancelEditWorker();
    } catch (e: any) {
      alert(e?.message || "Failed to update worker");
    }
  };

  const doDeleteWorker = async (r: ApiWorker) => {
    const ok = window.confirm(t("resources.workers.deleteConfirm", { name: r.name }));
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<{}> | ApiErr>("DELETE", `/api/resources/${r.id}`, {
        tenantId,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete worker");
        return;
      }

      setWorkers((prev) => prev.filter((w) => w.id !== r.id));
      await reloadResourcesFromApi?.();
      await Promise.allSettled([reloadWorkers(), reloadSubcontractors()]);
      if (editingWorkerId === r.id) cancelEditWorker();
    } catch (e: any) {
      alert(e?.message || "Failed to delete worker");
    }
  };

  const workersSorted = useMemo(() => {
    return [...workers].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [workers]);

  const workersVisible = useMemo(() => {
    const q = workerSearch.trim().toLowerCase();
    if (!q) return workersSorted;
    return workersSorted.filter((r) => {
      const hay = [r.id, r.name, r.role, String(r.rate ?? ""), r.phone]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [workersSorted, workerSearch]);

  /* ======================
     SUBCONTRACTORS (API-backed, same table as workers)
  ====================== */
  const [subcontractors, setSubcontractors] = useState<ApiWorker[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState<string | null>(null);

  const reloadSubcontractors = async () => {
    setSubsLoading(true);
    setSubsError(null);
    try {
      const res = await apiRequest<ApiOk<ApiWorker[]> | ApiErr>("GET", "/api/resources", {
        tenantId,
      });

      if ((res as any).ok) {
        const all = ((res as any).data || []) as ApiWorker[];
        setSubcontractors(all.filter((r) => Number(r.is_subcontractor) === 1));
      } else {
        setSubsError((res as any).error || "Failed to load subcontractors");
      }
    } catch (e: any) {
      setSubsError(e?.message || "Failed to load subcontractors");
    } finally {
      setSubsLoading(false);
    }
  };

  useEffect(() => {
    reloadSubcontractors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const [scName, setScName] = useState("");
  const [scCompanyName, setScCompanyName] = useState("");
  const [scPhone, setScPhone] = useState("");
  const [scEmail, setScEmail] = useState("");
  const [scNotes, setScNotes] = useState("");

  const [editingScId, setEditingScId] = useState<string | null>(null);
  const [editScName, setEditScName] = useState("");
  const [editScCompanyName, setEditScCompanyName] = useState("");
  const [editScPhone, setEditScPhone] = useState("");
  const [editScEmail, setEditScEmail] = useState("");
  const [editScNotes, setEditScNotes] = useState("");

  const handleAddSubcontractor = async () => {
    if (!scName.trim()) {
      alert(t("resources.subcontractors.nameRequired"));
      return;
    }

    try {
      const body = {
        name: scName.trim(),
        role: "",
        phone: scPhone.trim(),
        email: scEmail.trim(),
        company_name: scCompanyName.trim(),
        notes: scNotes.trim(),
        rate: null,
        is_subcontractor: 1,
      };

      const res = await apiRequest<ApiOk<ApiWorker> | ApiErr>("POST", "/api/resources", {
        tenantId,
        body,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to add subcontractor");
        return;
      }

      const row = (res as any).data as ApiWorker;
      setSubcontractors((prev) => [row, ...prev]);
      await reloadResourcesFromApi?.();
      await Promise.allSettled([reloadWorkers(), reloadSubcontractors()]);
      setScName("");
      setScCompanyName("");
      setScPhone("");
      setScEmail("");
      setScNotes("");
    } catch (e: any) {
      alert(e?.message || "Failed to add subcontractor");
    }
  };

  const startEditSubcontractor = (sc: ApiWorker) => {
    setEditingScId(sc.id);
    setEditScName(sc.name || "");
    setEditScCompanyName(sc.company_name || "");
    setEditScPhone(sc.phone || "");
    setEditScEmail(sc.email || "");
    setEditScNotes(sc.notes || "");
  };

  const cancelEditSubcontractor = () => {
    setEditingScId(null);
    setEditScName("");
    setEditScCompanyName("");
    setEditScPhone("");
    setEditScEmail("");
    setEditScNotes("");
  };

  const saveEditSubcontractor = async (sc: ApiWorker) => {
    if (!editScName.trim()) {
      alert(t("resources.subcontractors.nameRequired"));
      return;
    }

    try {
      const patch = {
        name: editScName.trim(),
        company_name: editScCompanyName.trim(),
        phone: editScPhone.trim(),
        email: editScEmail.trim(),
        notes: editScNotes.trim(),
        is_subcontractor: 1,
      };

      const res = await apiRequest<ApiOk<ApiWorker> | ApiErr>("PATCH", `/api/resources/${sc.id}`, {
        tenantId,
        body: patch,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to update subcontractor");
        return;
      }

      const updated = (res as any).data as ApiWorker;
      setSubcontractors((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      await reloadResourcesFromApi?.();
      await Promise.allSettled([reloadWorkers(), reloadSubcontractors()]);
      cancelEditSubcontractor();
    } catch (e: any) {
      alert(e?.message || "Failed to update subcontractor");
    }
  };

  const doDeleteSubcontractor = async (sc: ApiWorker) => {
    const ok = window.confirm(t("resources.subcontractors.deleteConfirm", { name: sc.name }));
    if (!ok) return;

    try {
      const res = await apiRequest<ApiOk<{}> | ApiErr>("DELETE", `/api/resources/${sc.id}`, {
        tenantId,
      });

      if (!(res as any).ok) {
        alert((res as any).error || "Failed to delete subcontractor");
        return;
      }

      setSubcontractors((prev) => prev.filter((r) => r.id !== sc.id));
      await reloadResourcesFromApi?.();
      await Promise.allSettled([reloadWorkers(), reloadSubcontractors()]);
      if (editingScId === sc.id) cancelEditSubcontractor();
    } catch (e: any) {
      alert(e?.message || "Failed to delete subcontractor");
    }
  };

  const subContractorsSorted = useMemo(() => {
    return [...subcontractors].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [subcontractors]);

  const subContractorsVisible = useMemo(() => {
    const q = scSearch.trim().toLowerCase();
    if (!q) return subContractorsSorted;
    return subContractorsSorted.filter((sc) => {
      const hay = [sc.id, sc.name, sc.company_name, sc.phone, sc.email, sc.notes]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [subContractorsSorted, scSearch]);

  return (
    <div className="container">
      <style>{`
        @media (max-width: 980px) { .resHideMd { display: none !important; } }
        @media (max-width: 680px) { .resHideSm { display: none !important; } }

        .resourcesLedger table { font-size: 13px; }
        .resourcesLedger thead th { padding: 10px 12px; }
        .resourcesLedger tbody td { padding: 10px 12px; }

        .tableIconBtn {
          height: 36px;
          width: 36px;
          display: grid;
          place-items: center;
          border-radius: 999px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          color: var(--text);
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
          transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
        }
        .tableIconBtn:hover {
          background: rgba(255,255,255,1);
          transform: translateY(-1px);
          border-color: rgba(15, 23, 42, 0.18);
        }
        .tableIconBtn:active { transform: translateY(0px); }

        .tableIconBtnDanger {
          border-color: rgba(229, 83, 61, 0.28);
          background: rgba(229, 83, 61, 0.08);
          color: var(--bad);
          box-shadow: none;
        }
        .tableIconBtnDanger:hover {
          background: rgba(229, 83, 61, 0.12);
          border-color: rgba(229, 83, 61, 0.40);
        }

        textarea.input{
          box-sizing: border-box;
          width: 100%;
          max-width: 100%;
        }
      `}</style>

      <div style={{ marginBottom: 12 }}>
        <h1 style={{ marginBottom: 6 }}>{t("resources.title")}</h1>
        <div className="pageNote">{t("resources.pageNote")}</div>
      </div>

      {/* ======================
         WORKERS / CREW (API)
      ====================== */}
      <div className="grid" style={{ gap: 14, marginBottom: 18 }}>
        <Card title={t("resources.workers.addTitle")} subtitle={<span><span className="reqStar">*</span> {t("common.required")}</span>}>
          <div
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              alignItems: "end",
            }}
          >
            <div className="field" style={{ minWidth: 0 }}>
              <label className="label">
                {t("resources.workers.nameLabel")} <span className="reqStar">*</span>
              </label>
              <input className="input" placeholder={t("resources.workers.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="field" style={{ minWidth: 0 }}>
              <label className="label">{t("resources.workers.roleLabel")}</label>
              <input className="input" placeholder={t("resources.workers.rolePlaceholder")} value={role} onChange={(e) => setRole(e.target.value)} />
            </div>

            <div className="field" style={{ minWidth: 0 }}>
              <label className="label">
                {t("resources.workers.dayRateLabel")} <span className="reqStar">*</span>
              </label>
              <input className="input" type="number" placeholder="0" value={dayRate} onChange={(e) => setDayRate(e.target.value)} />
            </div>
          </div>

          <div className="formActions">
            <button className="btn" onClick={handleAddWorker} type="button">{t("resources.workers.addBtn")}</button>
            <button
              className="btn btnSecondary"
              type="button"
              onClick={() => { setName(""); setRole(""); setDayRate(""); }}
            >
              {t("common.clear")}
            </button>
          </div>
        </Card>

        <div className="resourcesLedger">
          <TableShell
            title={t("resources.workers.listTitle")}
            subtitle={
              workersLoading
                ? t("resources.loading")
                : workersError
                ? `Error: ${workersError}`
                : t("resources.workers.workerCount", { count: workersVisible.length })
            }
            right={
              <div style={{ minWidth: 320, maxWidth: 520 }}>
                <label className="label">{t("common.search")}</label>
                <input
                  className="input"
                  placeholder={t("resources.workers.searchPlaceholder")}
                  value={workerSearch}
                  onChange={(e) => setWorkerSearch(e.target.value)}
                />
                <div style={{ marginTop: 8, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button className="btn btnSecondary btnSm" type="button" onClick={reloadWorkers}>
                    {t("resources.refresh")}
                  </button>
                </div>
              </div>
            }
          >
            <table>
              <thead>
                <tr>
                  <th style={{ width: 220 }}>{t("common.id")}</th>
                  <th>{t("resources.workers.workerHeader")}</th>
                  <th className="resHideSm" style={{ width: 240 }}>{t("resources.workers.roleLabel")}</th>
                  <th style={{ width: 140, textAlign: "right" }}>{t("resources.workers.dayRateHeader")}</th>
                  <th style={{ width: 140 }}>{t("common.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {workersVisible.map((r) => {
                  const isEditing = editingWorkerId === r.id;

                  return (
                    <tr key={r.id}>
                      <td>
                        <CellClamp lines={1} title={r.id}>{r.id}</CellClamp>
                      </td>

                      <td>
                        {isEditing ? (
                          <input className="input" value={editWorkerName} onChange={(e) => setEditWorkerName(e.target.value)} />
                        ) : (
                          <CellClamp lines={2} title={r.name || ""}>
                            <span style={{ fontWeight: 900 }}>{r.name}</span>
                          </CellClamp>
                        )}
                      </td>

                      <td className="resHideSm">
                        {isEditing ? (
                          <input className="input" value={editWorkerRole} onChange={(e) => setEditWorkerRole(e.target.value)} placeholder={t("resources.rolePlaceholder")} />
                        ) : (
                          r.role || "—"
                        )}
                      </td>

                      <td style={{ textAlign: "right" }}>
                        {isEditing ? (
                          <input className="input" type="number" value={editWorkerDayRate} onChange={(e) => setEditWorkerDayRate(e.target.value)} style={{ width: 140 }} />
                        ) : (
                          <span style={{ fontWeight: 1000 }}>{money0(Number(r.rate ?? 0))}</span>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <div className="actionsRow">
                            <button className="btn btnSm" onClick={() => saveEditWorker(r)} type="button">{t("common.save")}</button>
                            <button className="btn btnSecondary btnSm" onClick={cancelEditWorker} type="button">{t("common.cancel")}</button>
                          </div>
                        ) : (
                          <div className="actionsRow" style={{ gap: 10 }}>
                            <button type="button" className="tableIconBtn" onClick={() => startEditWorker(r)} title={t("common.edit")} aria-label={t("resources.workers.editAriaLabel")}>
                              <IconPencil />
                            </button>
                            <button type="button" className="tableIconBtn tableIconBtnDanger" onClick={() => doDeleteWorker(r)} title={t("common.delete")} aria-label={t("resources.workers.deleteAriaLabel")}>
                              <IconTrash />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!workersLoading && workersVisible.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                      {t("resources.workers.noWorkers")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableShell>

          <div className="noteOnCard" style={{ marginTop: 10 }}>
            Phase 1: Workers are now API-backed. Next we’ll add “blocked delete if used in wages” at the API layer.
          </div>
        </div>
      </div>

      {/* ======================
         SUBCONTRACTORS (API-backed)
      ====================== */}
      <div className="grid" style={{ gap: 14 }}>
        <Card title={t("resources.subcontractors.addTitle")} subtitle={<span><span className="reqStar">*</span> {t("common.required")}</span>}>
          <div style={{ display: "grid", gap: 14 }}>
            <div
              style={{
                display: "grid",
                gap: 14,
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                alignItems: "end",
              }}
            >
              <div className="field" style={{ minWidth: 0 }}>
                <label className="label">
                  {t("resources.subcontractors.nameLabel")} <span className="reqStar">*</span>
                </label>
                <input className="input" placeholder={t("resources.subcontractors.namePlaceholder")} value={scName} onChange={(e) => setScName(e.target.value)} />
              </div>

              <div className="field" style={{ minWidth: 0 }}>
                <label className="label">{t("resources.subcontractors.companyLabel")}</label>
                <input className="input" placeholder={t("resources.subcontractors.companyPlaceholder")} value={scCompanyName} onChange={(e) => setScCompanyName(e.target.value)} />
              </div>

              <div className="field" style={{ minWidth: 0 }}>
                <label className="label">{t("common.phone")}</label>
                <input className="input" placeholder={t("resources.subcontractors.phonePlaceholder")} value={scPhone} onChange={(e) => setScPhone(e.target.value)} />
              </div>

              <div className="field" style={{ minWidth: 0 }}>
                <label className="label">{t("common.email")}</label>
                <input className="input" placeholder={t("resources.subcontractors.emailPlaceholder")} value={scEmail} onChange={(e) => setScEmail(e.target.value)} />
              </div>
            </div>

            <div className="field" style={{ minWidth: 0 }}>
              <label className="label">{t("resources.subcontractors.notesLabel")}</label>
              <textarea
                className="input"
                placeholder={t("resources.subcontractors.notesPlaceholder")}
                value={scNotes}
                onChange={(e) => setScNotes(e.target.value)}
                style={{ minHeight: 86, resize: "vertical" }}
              />
            </div>
          </div>

          <div className="formActions">
            <button className="btn" onClick={handleAddSubcontractor} type="button">{t("resources.subcontractors.addBtn")}</button>
            <button
              className="btn btnSecondary"
              type="button"
              onClick={() => { setScName(""); setScCompanyName(""); setScPhone(""); setScEmail(""); setScNotes(""); }}
            >
              {t("common.clear")}
            </button>
            <button className="btn btnSecondary" type="button" onClick={reloadSubcontractors} disabled={subsLoading}>
              {subsLoading ? t("resources.refreshing") : t("resources.refresh")}
            </button>
            {subsError ? (
              <span style={{ marginLeft: 10, color: "var(--bad)", fontWeight: 700 }}>
                {subsError}
              </span>
            ) : null}
          </div>
        </Card>

        <div className="resourcesLedger">
          <TableShell
            title={t("resources.subcontractors.listTitle")}
            subtitle={
              subsLoading
                ? t("resources.loading")
                : subsError
                ? `Error: ${subsError}`
                : t("resources.subcontractors.subCount", { count: subContractorsVisible.length })
            }
            right={
              <div style={{ minWidth: 320, maxWidth: 520 }}>
                <label className="label">{t("common.search")}</label>
                <input
                  className="input"
                  placeholder={t("resources.subcontractors.searchPlaceholder")}
                  value={scSearch}
                  onChange={(e) => setScSearch(e.target.value)}
                />
              </div>
            }
          >
            <table>
              <thead>
                <tr>
                  <th style={{ width: 220 }}>{t("common.id")}</th>
                  <th style={{ minWidth: 220 }}>{t("common.name")}</th>
                  <th className="resHideSm" style={{ width: 220 }}>{t("resources.subcontractors.companyHeader")}</th>
                  <th className="resHideMd" style={{ width: 160 }}>{t("common.phone")}</th>
                  <th className="resHideSm" style={{ width: 220 }}>{t("common.email")}</th>
                  <th style={{ width: 260 }}>{t("common.notes")}</th>
                  <th style={{ width: 140 }}>{t("common.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {subContractorsVisible.map((sc) => {
                  const isEditing = editingScId === sc.id;

                  return (
                    <tr key={sc.id}>
                      <td>
                        <CellClamp lines={1} title={sc.id}>{sc.id}</CellClamp>
                      </td>

                      <td>
                        {isEditing ? (
                          <input className="input" value={editScName} onChange={(e) => setEditScName(e.target.value)} />
                        ) : (
                          <CellClamp lines={2} title={sc.name || ""}>
                            <span style={{ fontWeight: 900 }}>{sc.name}</span>
                          </CellClamp>
                        )}
                      </td>

                      <td className="resHideSm">
                        {isEditing ? (
                          <input className="input" value={editScCompanyName} onChange={(e) => setEditScCompanyName(e.target.value)} placeholder={t("resources.companyPlaceholder")} />
                        ) : (
                          sc.company_name || "—"
                        )}
                      </td>

                      <td className="resHideMd">
                        {isEditing ? (
                          <input className="input" value={editScPhone} onChange={(e) => setEditScPhone(e.target.value)} placeholder={t("resources.phonePlaceholder")} />
                        ) : (
                          sc.phone || "—"
                        )}
                      </td>

                      <td className="resHideSm">
                        {isEditing ? (
                          <input className="input" value={editScEmail} onChange={(e) => setEditScEmail(e.target.value)} placeholder={t("resources.emailPlaceholder")} />
                        ) : (
                          (sc.email || "—")
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <input className="input" value={editScNotes} onChange={(e) => setEditScNotes(e.target.value)} placeholder={t("resources.notesPlaceholder")} />
                        ) : (
                          <CellClamp lines={2} title={(sc.notes as any) || ""}>{(sc.notes as any) || "—"}</CellClamp>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <div className="actionsRow">
                            <button className="btn btnSm" onClick={() => saveEditSubcontractor(sc)} type="button">{t("common.save")}</button>
                            <button className="btn btnSecondary btnSm" onClick={cancelEditSubcontractor} type="button">{t("common.cancel")}</button>
                          </div>
                        ) : (
                          <div className="actionsRow" style={{ gap: 10 }}>
                            <button type="button" className="tableIconBtn" onClick={() => startEditSubcontractor(sc)} title={t("common.edit")} aria-label={t("resources.subcontractors.editAriaLabel")}>
                              <IconPencil />
                            </button>
                            <button type="button" className="tableIconBtn tableIconBtnDanger" onClick={() => doDeleteSubcontractor(sc)} title={t("common.delete")} aria-label={t("resources.subcontractors.deleteAriaLabel")}>
                              <IconTrash />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!subsLoading && subContractorsVisible.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                      {t("resources.subcontractors.noSubcontractors")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableShell>

          <div className="noteOnCard" style={{ marginTop: 10 }}>
            Subcontractors are now API-backed (same D1 table, <code>is_subcontractor = 1</code>).
          </div>
        </div>
      </div>
    </div>
  );
}
